import { Controller } from "@hotwired/stimulus"
import { callKarrotBridgeSafely } from "../../../lib/karrot_bridge_utils"
import SnackbarManager from "./utils/snackbar_manager"

// 조정안 화면의 수락/거절 응답을 처리하는 Stimulus 컨트롤러.
// 응답 제출이 성공하면 화면을 reload 해 서버가 결과 상태(view_state)에 맞는 패널을
// 렌더하게 한다. 결과 문구·수락 대기/완료 분기를 클라이언트가 다시 계산하지 않으므로
// 동시 응답 레이스에도 항상 서버 정본을 보여준다.
export default class extends Controller {
  static values = { url: String, inquiryUrl: String }
  static targets = [
    "panel",
    "actionFooter",
    "sheet",
    "acceptSheet",
    "amountInput",
    "detailInput",
    "otherInput",
    "rejectSubmit",
    "acceptSubmit",
    "snackbar",
  ]

  // 임계값 이상 아래로 끌면 닫는다.
  static DISMISS_THRESHOLD = 90

  connect() {
    // 플랫폼 클래스(.ios/.android)를 붙인다. 헤더는 공용 Vue #navbar가 그리므로,
    // 이 클래스는 navbar 스페이서 높이를 뺀 .mp 높이 계산(min-height calc)에 쓰인다.
    this.element.classList.add(window.platform || "web")
    if (this.hasSnackbarTarget) {
      this.snackbarManager = new SnackbarManager(this.snackbarTarget)
    }
    this.dragMove = this.dragMove.bind(this)
    this.dragEnd = this.dragEnd.bind(this)
  }

  // 완료 화면 "확인했어요": 웹뷰를 닫는다.
  close() {
    callKarrotBridgeSafely(
      (bridge) => bridge.closeRouter({}),
      { fallback: () => window.history.back() }
    )
  }

  openAccept() {
    this.showSheet(this.acceptSheetTarget)
  }

  confirmAccept() {
    this.submit({ decision: "accepted" })
  }

  openReject() {
    this.showSheet(this.sheetTarget)
  }

  showSheet(sheet) {
    const panel = sheet.querySelector(".mp-sheet__panel")
    if (panel) panel.style.transform = ""
    sheet.hidden = false
  }

  // dim/취소 버튼 공용 닫기. 클릭된 요소가 속한 시트를 닫는다.
  closeSheet(event) {
    const sheet = event.currentTarget.closest(".mp-sheet")
    if (sheet) sheet.hidden = true
  }

  // 수락 대기 화면 "조정안 다시 보기": 제안 상세를 읽기전용으로 다시 보여준다.
  viewProposal() {
    this.showPanel("proposal", { readOnly: true })
  }

  // 손잡이 영역 드래그 시작. pointer capture로 시트 밖으로 손가락이 나가도 추적한다.
  // 시트(수락/거절)를 가리지 않도록 손잡이가 속한 패널을 기준으로 동작한다.
  dragStart(event) {
    this.dragGrip = event.currentTarget
    this.dragPanel = this.dragGrip.closest(".mp-sheet__panel")
    this.dragSheet = this.dragGrip.closest(".mp-sheet")
    this.dragPointerId = event.pointerId
    this.dragStartY = event.clientY
    this.dragDeltaY = 0
    this.dragGrip.setPointerCapture(event.pointerId)
    this.dragPanel.style.transition = "none"
    this.dragGrip.addEventListener("pointermove", this.dragMove)
    this.dragGrip.addEventListener("pointerup", this.dragEnd)
    this.dragGrip.addEventListener("pointercancel", this.dragEnd)
  }

  dragMove(event) {
    this.dragDeltaY = Math.max(0, event.clientY - this.dragStartY)
    this.dragPanel.style.transform = `translateY(${this.dragDeltaY}px)`
  }

  dragEnd() {
    this.dragGrip.removeEventListener("pointermove", this.dragMove)
    this.dragGrip.removeEventListener("pointerup", this.dragEnd)
    this.dragGrip.removeEventListener("pointercancel", this.dragEnd)
    if (this.dragGrip.releasePointerCapture) {
      this.dragGrip.releasePointerCapture(this.dragPointerId)
    }
    this.dragPanel.style.transition = ""

    if (this.dragDeltaY > this.constructor.DISMISS_THRESHOLD) {
      this.dragPanel.style.transform = "translateY(100%)"
      const sheet = this.dragSheet
      const panel = this.dragPanel
      setTimeout(() => {
        sheet.hidden = true
        panel.style.transform = ""
      }, 220)
    } else {
      this.dragPanel.style.transform = ""
    }
  }

  // 사유 선택 시 해당 입력만 노출 + 선택 옵션 강조
  selectReason(event) {
    const code = event.target.value
    this.toggleInput(this.amountInputTarget, code === "amount")
    this.toggleInput(this.detailInputTarget, code === "return_item")
    this.toggleInput(this.otherInputTarget, code === "other")
    this.highlightSelectedOption(event.target)
  }

  // CSS :has() 미지원 webview를 대비해 선택 상태를 JS로 토글한다.
  highlightSelectedOption(selectedRadio) {
    this.element.querySelectorAll(".mp-option").forEach((option) => {
      const radio = option.querySelector(".mp-option__radio")
      option.classList.toggle("mp-option--selected", radio === selectedRadio)
    })
  }

  reject() {
    const checked = this.element.querySelector('input[name="reject_reason_code"]:checked')
    if (!checked) {
      this.toast("거절 사유를 선택해 주세요.")
      return
    }

    const code = checked.value
    const body = { decision: "rejected", reject_reason_code: code }

    if (code === "amount") {
      const amount = this.amountInputTarget.value.replace(/[^0-9]/g, "")
      if (!amount) {
        this.toast("원하는 조정 금액을 입력해 주세요.")
        return
      }
      body.reject_desired_amount = amount
    } else if (code === "return_item") {
      body.reject_detail = this.detailInputTarget.value.trim()
    } else {
      const detail = this.otherInputTarget.value.trim()
      if (!detail) {
        this.toast("거절 사유를 입력해 주세요.")
        return
      }
      body.reject_detail = detail
    }

    this.submit(body)
  }

  continueInquiry() {
    const path = this.inquiryUrlValue
    if (!path) {
      callKarrotBridgeSafely(
        (bridge) => bridge.closeRouter({}),
        { fallback: () => window.history.back() }
      )
      return
    }

    const remote = new URL(path, window.location.origin).href
    callKarrotBridgeSafely(
      (bridge) => bridge.pushRouter({ router: { remote, navbar: true } }),
      { fallback: () => { window.location.href = remote } }
    )
  }

  submit(body) {
    this.setSubmitting(true)

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // 결과 화면은 서버가 view_state로 렌더한다. reload 해 정본 상태를 보여준다.
          window.location.reload()
        } else {
          this.toast(data.error || "처리 중 오류가 발생했어요.")
          this.setSubmitting(false)
        }
      })
      .catch(() => {
        this.toast("처리 중 오류가 발생했어요.")
        this.setSubmitting(false)
      })
  }

  // 패널(제안 상세/결과)을 전환한다. 현재는 "조정안 다시 보기"(읽기전용 제안)만 사용한다.
  showPanel(name, { readOnly = false } = {}) {
    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== name
    })
    if (this.hasActionFooterTarget) {
      this.actionFooterTarget.hidden = readOnly
    }
  }

  toggleInput(input, visible) {
    input.hidden = !visible
    if (!visible) {
      input.value = ""
    }
  }

  setSubmitting(submitting) {
    if (this.hasAcceptSubmitTarget) {
      this.acceptSubmitTarget.disabled = submitting
    }
    if (this.hasRejectSubmitTarget) {
      this.rejectSubmitTarget.disabled = submitting
    }
  }

  toast(message) {
    if (this.snackbarManager) {
      this.snackbarManager.show(message)
    }
  }
}
